import React, { useState, useEffect } from 'react';
import { 
  Wrench, DollarSign, AlertTriangle, Clock, Calendar, 
  ChevronRight, CheckCircle, Info, TrendingUp, Shield,
  Edit2, Save, X, Plus, FileText, Car, RefreshCw, Loader,
  MapPin, Calculator, Phone, Star, ExternalLink
} from 'lucide-react';
import vehicleDbAPI from '../../src/services/vehicleDBAPI';
import MileageUpdateComponent from './MileageUpdateComponent';
import { api } from '../services/api';
import logger from '../utils/logger';

/**
 * Render a cost range, or an em dash when there is no estimate.
 *
 * The backend sends `null` for a service it has no estimate for (#131). Four
 * sites in this file used to read `cost?.low` directly: three printed
 * "$undefined-$undefined" and one printed "$0-$0", which reads as free
 * servicing. An all-zero range is also treated as no estimate -- cost_high === 0
 * was the old "estimation failed" sentinel.
 */
export function formatCostRange(cost) {
  if (!cost) return '—';
  const { low, high } = cost;
  if (typeof low !== 'number' || typeof high !== 'number') return '—';
  if (high <= 0) return '—';
  return `$${low}-$${high}`;
}

// // Mock API service - replace with your actual API service
// const api = {
//   get: async (url, config) => {
//     // Simulate API call
//     console.log('API GET:', url, config);
//     return { data: getMockMaintenanceData() };
//   },
//   put: async (url, data) => {
//     console.log('API PUT:', url, data);
//     return { data: { success: true, new_mileage: data.new_mileage } };
//   },
//   post: async (url, data) => {
//     console.log('API POST:', url, data);
//     return { data: { success: true } };
//   }
// };

export default function VehicleMaintenance({ 
    vehicleProfileId = 1, 
    vin = null,
    year = null,
    make = null,
    model = null,
    trim = null,
    initialMileage = 45000,
    onMileageUpdate = null
  }) {
  // Debug: Log the props being received
  logger.log('VehicleMaintenance props:', { vehicleProfileId, vin, year, make, model, trim, initialMileage });
  logger.log('VehicleMaintenance: VIN available:', !!vin);
  logger.log('VehicleMaintenance: YMM available:', !!(year && make && model));
  logger.log('VehicleMaintenance: Trim available:', !!trim);
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('upcoming');
  const [expandedService, setExpandedService] = useState(null);
  const [currentMileage, setCurrentMileage] = useState(initialMileage);
  const [completingService, setCompletingService] = useState(null);
  const [completingServiceLoading, setCompletingServiceLoading] = useState(false);
  const [findingMechanicService, setFindingMechanicService] = useState(null);
  const [estimateService, setEstimateService] = useState(null);

  // Fetch maintenance data using cached endpoint
  const fetchMaintenanceData = async (forceRefresh = false) => {
    // Check if we have a vehicle profile ID
    if (!vehicleProfileId) {
      setError('Vehicle profile ID is required');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      logger.log('Fetching maintenance schedule with cache for vehicle:', vehicleProfileId);
      
      // Use the new cached endpoint
      const response = await vehicleDbAPI.maintenance.getScheduleWithCache(vehicleProfileId, forceRefresh);
      logger.log('Cached maintenance response:', response);
      
      setData(response);
      
      // Update current mileage if provided in response
      if (response.vehicle_info?.current_mileage) {
        setCurrentMileage(response.vehicle_info.current_mileage);
      }
    } catch (err) {
      console.error('Error fetching maintenance data:', err);
      
      // Check if it's a subscription/payment error
      if (err.response?.status === 402 || err.response?.data?.upgrade_required) {
        setData({
          upgrade_required: true,
          message: err.response?.data?.message || 'Upgrade to access full maintenance data',
          limited_data: err.response?.data?.limited_data
        });
      } else if (err.response?.status === 403) {
        // Subscription required error
        setData({
          upgrade_required: true,
          message: 'Maintenance schedules require a subscription. Please upgrade to access this feature.',
          limited_data: null,
          subscription_required: true
        });
      } else {
        setError(err.message || 'Failed to load maintenance data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch data if we have a vehicle profile ID
    if (vehicleProfileId) {
      logger.log('VehicleMaintenance: Fetching data for vehicle profile:', vehicleProfileId);
      fetchMaintenanceData();
    } else {
      logger.log('VehicleMaintenance: No vehicle profile ID, skipping fetch');
    }
  }, [vehicleProfileId]);

  // Refresh data when mileage changes significantly
  useEffect(() => {
    if (Math.abs(currentMileage - initialMileage) > 5000) {
      fetchMaintenanceData();
    }
  }, [currentMileage]);

  // Mark service as completed
  const handleServiceComplete = async (serviceData) => {
    setCompletingServiceLoading(true);
    try {
      // Save to backend if available
      if (vehicleProfileId) {
        try {
          const result = await vehicleDbAPI.maintenance.completeService(vehicleProfileId, {
            service_name: serviceData.service,
            mileage: parseInt(serviceData.mileage),
            cost: parseFloat(serviceData.cost) || null,
            provider: serviceData.provider || null,
            notes: serviceData.notes || null,
            invoice_number: serviceData.invoice_number || null,
          completion_date: new Date().toISOString().split('T')[0]
        });
        logger.log('Service completion result:', result);
      } catch (err) {
        logger.error('Could not save completion to backend:', err);
          // Show user-friendly error message
          alert('Failed to save service completion. Please try again.');
          setCompletingServiceLoading(false);
          return;
        }
      }
      
      setCompletingService(null);
      setCompletingServiceLoading(false);
      fetchMaintenanceData(); // Refresh data
    } catch (err) {
      console.error('Failed to mark service completed:', err);
      setCompletingServiceLoading(false);
      alert('Failed to mark service as completed. Please try again.');
    }
  };

  // Check if we have vehicle profile ID
  if (!vehicleProfileId) {
    return (
      <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
        <div className="text-center py-8">
          <Car className="w-12 h-12 text-ink-muted mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-ink-soft mb-2">Vehicle Profile Required</h3>
          <p className="text-ink-muted mb-4">
            A vehicle profile ID is required to view the maintenance schedule.
          </p>
          <div className="text-sm text-ink-muted space-y-1">
            <p><strong>Available data:</strong></p>
            <p>Vehicle Profile ID: {vehicleProfileId || 'Not provided'}</p>
            <p>VIN: {vin || 'Not provided'}</p>
            <p>Year: {year || 'Not provided'}, Make: {make || 'Not provided'}, Model: {model || 'Not provided'}</p>
            <p>Trim: {trim || 'Not provided'}</p>
            <p>Mileage: {initialMileage || 'Not provided'}</p>
          </div>
          <div className="mt-4 text-small text-ink-muted">
            <p>This component requires a valid vehicle profile ID to fetch maintenance data.</p>
            <p>Please ensure the vehicle profile is properly configured.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <MaintenanceLoadingSkeleton />;
  }

  if (error && !data) {
    return (
      <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
        <div className="text-center py-8">
          <AlertTriangle className="w-12 h-12 text-danger mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-ink-soft mb-2">Failed to Load Maintenance Data</h3>
          <p className="text-ink-muted mb-4">{error}</p>
          <div className="text-sm text-ink-muted space-y-1 mb-4">
            <p><strong>Vehicle data used:</strong></p>
            <p>VIN: {vin || 'Not provided'}</p>
            <p>Year: {year || 'Not provided'}, Make: {make || 'Not provided'}, Model: {model || 'Not provided'}</p>
            <p>Trim: {trim || 'Not provided'}</p>
          </div>
          <button
            onClick={() => fetchMaintenanceData(true)}
            className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover transition-colors"
          >
            <RefreshCw className="w-4 h-4 inline mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (data?.upgrade_required) {
    return <MaintenanceUpgradePrompt data={data} />;
  }

  const tabs = [
    { id: 'upcoming', label: 'Upcoming', count: data?.upcoming_services?.length || 0 },
    { id: 'overdue', label: 'Overdue', count: data?.overdue_services?.length || 0 },
    { id: 'schedule', label: 'Full Schedule', count: data?.maintenance_schedule?.length || 0 },
    { id: 'history', label: 'History', count: data?.completed_services?.length || 0 },
    { id: 'costs', label: 'Cost Analysis', icon: DollarSign }
  ];

  return (
    <div className="space-y-6">
      {/* Header with vehicle info and editable mileage */}
      <div className="bg-surface-card rounded-card shadow-elevation-1 p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-bold text-ink-soft">Maintenance Schedule</h2>
            {data?.vehicle_info && (
              <p className="text-sm text-ink-muted mt-1 break-words">
                {data.vehicle_info.year} {data.vehicle_info.make} {data.vehicle_info.model} {data.vehicle_info.trim}
              </p>
            )}
            <div className="mt-4">
              <MileageUpdateComponent
                vehicleId={vehicleProfileId}
                currentMileage={currentMileage}
                onMileageUpdate={(newMileage) => {
                  setCurrentMileage(newMileage);
                  if (onMileageUpdate) {
                    onMileageUpdate(newMileage);
                  }
                  fetchMaintenanceData();
                }}
                showUnitToggle={true}
                className="border-0 p-0"
              />
            </div>
            {data?.data_source && (
              <p className="text-small text-ink-muted mt-1">
                Data source: {data.data_source.replace('_', ' ')}
              </p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <button
              onClick={() => fetchMaintenanceData(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-surface-muted hover:bg-surface-muted rounded-lg transition w-full sm:w-auto justify-center"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {data?.severe_driving_adjustments && (
              <button className="text-sm text-accent hover:text-accent-hover flex items-center gap-1 w-full sm:w-auto justify-center sm:justify-start">
                <Info className="w-4 h-4" />
                <span className="hidden sm:inline">Severe driving conditions</span>
                <span className="sm:hidden">Severe driving</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Overdue Alert */}
      {data?.overdue_services?.length > 0 && selectedTab !== 'overdue' && (
        <OverdueAlert 
          services={data.overdue_services} 
          onViewAll={() => setSelectedTab('overdue')} 
        />
      )}

      {/* Tabs */}
      <div className="bg-surface-card rounded-card shadow-elevation-1">
        <div className="border-b">
          <nav className="flex -mb-px overflow-x-auto scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id)}
                className={`flex-shrink-0 px-3 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  selectedTab === tab.id
                    ? 'border-accent text-accent'
                    : 'border-transparent text-ink-muted hover:text-ink-soft'
                }`}
              >
                <div className="flex items-center gap-1 sm:gap-2">
                  {tab.icon && <tab.icon className="w-4 h-4" />}
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">
                    {tab.id === 'upcoming' ? 'Due' :
                     tab.id === 'overdue' ? 'Overdue' :
                     tab.id === 'schedule' ? 'Schedule' :
                     tab.id === 'history' ? 'History' :
                     tab.id === 'costs' ? 'Costs' : tab.label}
                  </span>
                  {tab.count > 0 && (
                    <span className={`ml-1 px-1.5 sm:px-2 py-0.5 text-small rounded-full ${
                      tab.id === 'overdue'
                        ? 'bg-danger-tint text-danger'
                        : 'bg-surface-muted text-ink-soft'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {selectedTab === 'upcoming' && (
            <UpcomingServices 
              services={data.upcoming_services} 
              expanded={expandedService}
              onToggle={setExpandedService}
              onComplete={setCompletingService}
              onFindMechanic={setFindingMechanicService}
              onGetEstimate={setEstimateService}
              currentMileage={currentMileage}
            />
          )}
          
          {selectedTab === 'overdue' && (
            <OverdueServices 
              services={data.overdue_services}
              expanded={expandedService}
              onToggle={setExpandedService}
              onComplete={setCompletingService}
              onFindMechanic={setFindingMechanicService}
              onGetEstimate={setEstimateService}
              currentMileage={currentMileage}
            />
          )}
          
          {selectedTab === 'schedule' && (
            <FullSchedule 
              schedule={data.maintenance_schedule}
              categories={data.service_categories}
              currentMileage={currentMileage}
            />
          )}
          
          {selectedTab === 'history' && (
            <MaintenanceHistory 
              history={data.completed_services}
              vehicleProfileId={vehicleProfileId}
            />
          )}
          
          {selectedTab === 'costs' && (
            <CostAnalysis 
              analysis={data.maintenance_cost_analysis}
              nextMajor={data.next_major_service}
            />
          )}
        </div>
      </div>

      {/* Manufacturer Recommendations */}
      {data?.manufacturer_recommendations?.length > 0 && (
        <ManufacturerNotes notes={data.manufacturer_recommendations} />
      )}

      {/* Service Completion Modal */}
      {completingService && (
        <ServiceCompletionModal
          service={completingService}
          currentMileage={currentMileage}
          onComplete={handleServiceComplete}
          onCancel={() => setCompletingService(null)}
          loading={completingServiceLoading}
        />
      )}

      {/* Find Mechanic Modal */}
      {findingMechanicService && (
        <FindMechanicModal
          service={findingMechanicService}
          vehicleInfo={{ year, make, model, vin }}
          onClose={() => setFindingMechanicService(null)}
        />
      )}

      {/* Get Estimate Modal */}
      {estimateService && (
        <EstimateModal
          service={estimateService}
          vehicleInfo={{ year, make, model, vin, mileage: currentMileage }}
          onClose={() => setEstimateService(null)}
        />
      )}
    </div>
  );
}

// Sub-components remain largely the same but with added functionality

function OverdueAlert({ services, onViewAll }) {
    const criticalCount = services.filter(s => s.risk_level >= 4).length;
    
    return (
      <div className="bg-danger-tint border border-danger-border rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-danger mt-0.5 mr-3 flex-shrink-0" />
          <div className="flex-grow">
            <h3 className="font-semibold text-danger">
              {services.length} Overdue Service{services.length > 1 ? 's' : ''}
              {criticalCount > 0 && ` (${criticalCount} critical)`}
            </h3>
            <p className="text-sm text-danger mt-1">
              These services are past due and should be addressed soon to avoid potential issues.
            </p>
            <button
              onClick={onViewAll}
              className="mt-2 text-sm text-danger hover:text-danger font-medium flex items-center gap-1"
            >
              View all overdue services
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

function UpcomingServices({ services, expanded, onToggle, onComplete, onFindMechanic, onGetEstimate, currentMileage }) {
    if (!services || services.length === 0) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="w-12 h-12 text-success mx-auto mb-3" />
          <p className="text-ink-muted">No services due in the next 5,000 miles</p>
        </div>
      );
    }
  
    return (
      <div className="space-y-3">
        {services.map((service, idx) => (
          <ServiceCard
            key={idx}
            service={service}
            isExpanded={expanded === idx}
            onToggle={() => onToggle(expanded === idx ? null : idx)}
            onComplete={() => onComplete(service)}
            onFindMechanic={() => onFindMechanic(service)}
            onGetEstimate={() => onGetEstimate(service)}
            type="upcoming"
            currentMileage={currentMileage}
          />
        ))}
      </div>
    );
  }

  function OverdueServices({ services, expanded, onToggle, onComplete, onFindMechanic, onGetEstimate, currentMileage }) {
    if (!services || services.length === 0) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="w-12 h-12 text-success mx-auto mb-3" />
          <p className="text-ink-muted">All maintenance is up to date!</p>
        </div>
      );
    }
  
    return (
      <div className="space-y-3">
        {services.map((service, idx) => (
          <ServiceCard
            key={idx}
            service={service}
            isExpanded={expanded === idx}
            onToggle={() => onToggle(expanded === idx ? null : idx)}
            onComplete={() => onComplete(service)}
            onFindMechanic={() => onFindMechanic(service)}
            onGetEstimate={() => onGetEstimate(service)}
            type="overdue"
            currentMileage={currentMileage}
          />
        ))}
      </div>
    );
  }

  function ServiceCard({ service, isExpanded, onToggle, onComplete, onFindMechanic, onGetEstimate, type, currentMileage }) {
    const getRiskClasses = (level) => {
      if (level >= 4) return 'bg-danger-tint text-danger';
      if (level >= 3) return 'bg-warning-tint text-warning';
      if (level >= 2) return 'bg-warning-tint text-warning';
      return 'bg-surface-sunken text-ink-soft';
    };

    const getSeverityClasses = (severity) => {
      switch (severity) {
        case 'critical': return 'bg-danger-tint text-danger';
        case 'high': return 'bg-warning-tint text-warning';
        case 'medium': return 'bg-warning-tint text-warning';
        default: return 'bg-surface-sunken text-ink-soft';
      }
    };

    const riskClasses = type === 'overdue' ? getRiskClasses(service.risk_level) : getSeverityClasses(service.severity);

    return (
      <div className={`border rounded-lg overflow-hidden ${
        type === 'overdue' ? 'border-danger-border bg-danger-tint' : 'border-border-default'
      }`}>
        <div className="p-3 sm:p-4">
          {/* Main content area */}
          <div className="flex flex-col gap-3">
            {/* Service info row */}
            <button
              onClick={onToggle}
              className="flex items-start gap-3 text-left w-full"
            >
              <Wrench className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                type === 'overdue' ? 'text-danger' : 'text-ink-muted'
              }`} />
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-ink-soft break-words">{service.service}</h4>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1 text-sm text-ink-muted">
                  {type === 'upcoming' ? (
                    <>
                      <span>Due in {service.miles_until_due?.toLocaleString()} miles</span>
                      <span className="hidden sm:inline">At {service.due_at_miles?.toLocaleString()} miles</span>
                    </>
                  ) : (
                    <>
                      <span className="text-danger">
                        Overdue by {service.overdue_by_miles?.toLocaleString()} miles
                      </span>
                      <span className="hidden sm:inline">Was due at {service.due_at_miles?.toLocaleString()} miles</span>
                    </>
                  )}
                </div>
              </div>
              <ChevronRight className={`w-4 h-4 text-ink-muted transition-transform flex-shrink-0 sm:hidden ${
                isExpanded ? 'rotate-90' : ''
              }`} />
            </button>
            
            {/* Cost, badge, and actions row */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Cost estimate */}
              <div className="flex-shrink-0">
                <p className="font-medium text-sm">
                  {formatCostRange(service.estimated_cost)}
                </p>
                {service.time_estimate && (
                  <p className="text-small text-ink-muted">{service.time_estimate} hrs</p>
                )}
              </div>
              
              {/* Severity/Risk badge */}
              <span className={`px-2 py-1 text-small rounded-full flex-shrink-0 ${riskClasses}`}>
                {type === 'overdue' ? `Risk: ${service.risk_level}/5` : service.severity}
              </span>
              
              {/* Spacer for desktop */}
              <div className="hidden sm:block flex-1"></div>
              
              {/* Action buttons - wrap on mobile */}
              <div className="flex flex-wrap gap-1.5 w-full sm:w-auto mt-1 sm:mt-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onFindMechanic();
                  }}
                  className="px-2 py-1.5 text-small bg-accent text-white rounded hover:bg-accent-hover transition flex items-center gap-1 flex-1 sm:flex-none justify-center sm:justify-start"
                  title="Find a mechanic near you"
                >
                  <MapPin className="w-3 h-3" />
                  <span>Mechanic</span>
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onGetEstimate();
                  }}
                  className="px-2 py-1.5 text-small bg-info text-white rounded hover:bg-info/80 transition flex items-center gap-1 flex-1 sm:flex-none justify-center sm:justify-start"
                  title="Get AI-generated estimate"
                >
                  <Calculator className="w-3 h-3" />
                  <span>Estimate</span>
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onComplete();
                  }}
                  className="px-2 py-1.5 text-small bg-success text-white rounded hover:bg-success/80 transition flex items-center gap-1 flex-1 sm:flex-none justify-center sm:justify-start"
                  title="Mark as complete"
                >
                  <CheckCircle className="w-3 h-3" />
                  <span>Done</span>
                </button>
              </div>
            </div>
          </div>
          
          {isExpanded && (
            <div className="mt-4 pt-4 border-t">
              {service.can_diy && (
                <div className="mb-3 flex items-center gap-2 text-sm text-success">
                  <CheckCircle className="w-4 h-4" />
                  <span>Can be done DIY</span>
                </div>
              )}
              
              {type === 'overdue' && service.risk_level >= 3 && (
                <div className="mb-3 p-3 bg-danger-tint rounded">
                  <p className="text-sm font-medium text-danger mb-1">Potential consequences:</p>
                  <ul className="text-sm text-danger space-y-0.5">
                    <li>• Increased wear on related components</li>
                    <li>• Reduced fuel efficiency</li>
                    <li>• Potential for breakdowns</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  function MaintenanceHistory({ history, vehicleProfileId }) {
    if (!history || history.length === 0) {
      return (
        <div className="text-center py-8">
          <FileText className="w-12 h-12 text-ink-muted mx-auto mb-3" />
          <p className="text-ink-muted">No maintenance history recorded yet</p>
        </div>
      );
    }
  
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold mb-4">Completed Services</h3>
        <div className="space-y-3">
          {history.map((item, idx) => (
            <div key={idx} className="border rounded-lg p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-ink-soft">{item.service_name}</h4>
                  <p className="text-sm text-ink-muted mt-1">
                    Completed at {item.completed_mileage?.toLocaleString()} miles
                  </p>
                  {item.completed_at && (
                    <p className="text-sm text-ink-muted">
                      {new Date(item.completed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  {item.actual_cost && (
                    <p className="font-medium">${item.actual_cost}</p>
                  )}
                  {item.service_provider && (
                    <p className="text-small text-ink-muted">{item.service_provider}</p>
                  )}
                </div>
              </div>
              {item.notes && (
                <p className="mt-2 text-sm text-ink-muted italic">{item.notes}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  function ServiceCompletionModal({ service, currentMileage, onComplete, onCancel, loading = false }) {
    const [formData, setFormData] = useState({
      mileage: currentMileage,
      cost: service.estimated_cost?.average || '',
      provider: '',
      notes: '',
      invoice_number: ''
    });
  
    const handleSubmit = () => {
      // Basic validation
      if (!formData.mileage || formData.mileage <= 0) {
        alert('Please enter a valid mileage');
        return;
      }
      
      onComplete({
        ...service,
        ...formData
      });
    };
  
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-surface-card rounded-card shadow-elevation-1 p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4">Complete Service</h3>
          <p className="text-ink-muted mb-4">{service.service}</p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                Mileage at Service
              </label>
              <input
                type="number"
                value={formData.mileage}
                onChange={(e) => setFormData({...formData, mileage: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent border-border-strong"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                Actual Cost
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.cost}
                onChange={(e) => setFormData({...formData, cost: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent border-border-strong"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                Service Provider
              </label>
              <input
                type="text"
                value={formData.provider}
                onChange={(e) => setFormData({...formData, provider: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent border-border-strong"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                Invoice Number
              </label>
              <input
                type="text"
                value={formData.invoice_number}
                onChange={(e) => setFormData({...formData, invoice_number: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent border-border-strong"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent border-border-strong"
                rows="3"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`flex-1 py-2 rounded-lg transition flex items-center justify-center ${
                  loading 
                    ? 'bg-ink-muted text-ink-muted cursor-not-allowed' 
                    : 'bg-success text-white hover:bg-success/80'
                }`}
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Mark Complete'
                )}
              </button>
              <button
                onClick={onCancel}
                disabled={loading}
                className={`flex-1 py-2 rounded-lg transition ${
                  loading 
                    ? 'bg-surface-muted text-ink-muted cursor-not-allowed' 
                    : 'bg-surface-muted text-ink-soft hover:bg-surface-muted'
                }`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Find Mechanic Modal Component
  function FindMechanicModal({ service, vehicleInfo, onClose }) {
    const [loading, setLoading] = useState(false);
    const [mechanics, setMechanics] = useState([]);
    const [location, setLocation] = useState('');
    const [error, setError] = useState(null);
    const [searching, setSearching] = useState(false);

    const handleSearch = async () => {
      if (!location.trim()) {
        setError('Please enter a location (city, zip code, or address)');
        return;
      }

      setSearching(true);
      setError(null);
      
      try {
        const response = await api.post('/maintenance/find-mechanics', {
          location: location.trim(),
          service: service.service,
          vehicle_make: vehicleInfo.make,
          vehicle_model: vehicleInfo.model
        });
        
        setMechanics(response.data.mechanics || []);
      } catch (err) {
        console.error('Error finding mechanics:', err);
        setError(err.response?.data?.detail || 'Failed to find mechanics. Please try again.');
      } finally {
        setSearching(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-surface-card rounded-card shadow-elevation-1 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-section-title font-semibold flex items-center gap-2">
              <MapPin className="w-5 h-5 text-accent" />
              Find a Mechanic
            </h3>
            <button
              onClick={onClose}
              className="text-ink-muted hover:text-ink-muted transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-4">
            <p className="text-ink-muted mb-2">
              Service: <span className="font-medium">{service.service}</span>
            </p>
            <p className="text-sm text-ink-muted">
              Vehicle: {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
            </p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-ink-soft mb-2">
              Enter your location
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="City, ZIP code, or address"
                className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent border-border-strong"
              />
              <button
                onClick={handleSearch}
                disabled={searching || !location.trim()}
                className="px-6 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {searching ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <MapPin className="w-4 h-4" />
                    Search
                  </>
                )}
              </button>
            </div>
            {error && (
              <p className="mt-2 text-sm text-danger">{error}</p>
            )}
          </div>

          {mechanics.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-ink-soft">
                Found {mechanics.length} mechanic{mechanics.length !== 1 ? 's' : ''} nearby
              </h4>
              {mechanics.map((mechanic, idx) => (
                <div key={idx} className="border rounded-lg p-4 transition hover:border-border-strong">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h5 className="font-semibold text-ink-soft">{mechanic.name}</h5>
                      {mechanic.rating && (
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="w-4 h-4 text-warning fill-current" />
                          <span className="text-sm text-ink-soft">
                            {mechanic.rating} {mechanic.review_count && `(${mechanic.review_count} reviews)`}
                          </span>
                        </div>
                      )}
                    </div>
                    {mechanic.distance && (
                      <span className="text-sm text-ink-muted">{mechanic.distance}</span>
                    )}
                  </div>
                  
                  {mechanic.address && (
                    <p className="text-sm text-ink-muted mb-2 flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {mechanic.address}
                    </p>
                  )}
                  
                  {mechanic.phone && (
                    <p className="text-sm text-ink-muted mb-2 flex items-center gap-1">
                      <Phone className="w-3.5 h-3.5" />
                      {mechanic.phone}
                    </p>
                  )}

                  <div className="flex gap-2 mt-3">
                    {mechanic.website && (
                      <a
                        href={mechanic.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-accent hover:text-accent-hover flex items-center gap-1"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Website
                      </a>
                    )}
                    {mechanic.directions_url && (
                      <a
                        href={mechanic.directions_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-accent hover:text-accent-hover flex items-center gap-1"
                      >
                        <MapPin className="w-3.5 h-3.5" />
                        Directions
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {mechanics.length === 0 && !searching && location && (
            <div className="text-center py-8 text-ink-muted">
              <MapPin className="w-12 h-12 mx-auto mb-3 text-ink-muted" />
              <p>No mechanics found. Try a different location.</p>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-surface-muted text-ink-soft rounded-lg hover:bg-surface-muted transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Estimate Modal Component
  function EstimateModal({ service, vehicleInfo, onClose }) {
    const [loading, setLoading] = useState(false);
    const [estimate, setEstimate] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
      fetchEstimate();
    }, []);

    const fetchEstimate = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await api.post('/maintenance/generate-estimate', {
          service: service.service,
          vehicle_year: vehicleInfo.year,
          vehicle_make: vehicleInfo.make,
          vehicle_model: vehicleInfo.model,
          vehicle_trim: vehicleInfo.trim,
          current_mileage: vehicleInfo.mileage,
          estimated_cost_low: service.estimated_cost?.low,
          estimated_cost_high: service.estimated_cost?.high,
          time_estimate: service.time_estimate
        });
        
        setEstimate(response.data);
      } catch (err) {
        console.error('Error generating estimate:', err);
        setError(err.response?.data?.detail || 'Failed to generate estimate. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-surface-card rounded-card shadow-elevation-1 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-section-title font-semibold flex items-center gap-2">
              <Calculator className="w-5 h-5 text-info" />
              Service Estimate
            </h3>
            <button
              onClick={onClose}
              className="text-ink-muted hover:text-ink-muted transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-4">
            <p className="text-ink-muted mb-2">
              Service: <span className="font-medium">{service.service}</span>
            </p>
            <p className="text-sm text-ink-muted">
              Vehicle: {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
            </p>
          </div>

          {loading && (
            <div className="text-center py-12">
              <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-info" />
              <p className="text-ink-muted">Generating AI estimate...</p>
            </div>
          )}

          {error && (
            <div className="bg-danger-tint border border-danger-border rounded-lg p-4 mb-4">
              <p className="text-danger">{error}</p>
            </div>
          )}

          {estimate && !loading && (
            <div className="space-y-4">
              <div className="bg-info-tint border border-info-border rounded-lg p-4">
                <h4 className="font-semibold text-info mb-3">Estimated Cost Breakdown</h4>
                
                {estimate.parts && estimate.parts.length > 0 && (
                  <div className="mb-4">
                    <h5 className="font-medium text-ink-soft mb-2">Parts:</h5>
                    <div className="space-y-2">
                      {estimate.parts.map((part, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-ink-soft">{part.name}</span>
                          <span className="font-medium">${part.cost?.toFixed(2) || '0.00'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {estimate.labor != null && (
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-ink-soft">Labor ({estimate.labor_hours != null ? estimate.labor_hours : (estimate.time_estimate || 1)} hours @ ${estimate.labor_rate || 100}/hr)</span>
                      <span className="font-medium">${estimate.labor?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                )}

                <div className="border-t border-info-border pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-lg text-ink-soft">Total Estimate:</span>
                    <span className="font-bold text-xl text-info">
                      ${estimate.total_cost?.toFixed(2) || estimate.estimated_total?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  {estimate.cost_range && (
                    <p className="text-sm text-ink-muted mt-1 text-right">
                      Range: ${estimate.cost_range.low} - ${estimate.cost_range.high}
                    </p>
                  )}
                </div>
              </div>

              {estimate.notes && (
                <div className="bg-info-tint border border-info-border rounded-lg p-4">
                  <h5 className="font-medium text-info mb-2">Additional Notes:</h5>
                  <p className="text-sm text-info whitespace-pre-wrap">{estimate.notes}</p>
                </div>
              )}

              {estimate.warranty_info && (
                <div className="bg-success-tint border border-success-border rounded-lg p-4">
                  <h5 className="font-medium text-success mb-2 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Warranty Information:
                  </h5>
                  <p className="text-sm text-success">{estimate.warranty_info}</p>
                </div>
              )}

              <div className="text-small text-ink-muted italic pt-2">
                * This is an AI-generated estimate. Actual costs may vary based on location, shop rates, and vehicle condition.
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-surface-muted text-ink-soft rounded-lg hover:bg-surface-muted transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  function FullSchedule({ schedule, categories, currentMileage }) {
    const [selectedCategory, setSelectedCategory] = useState('all');
    
    const filteredSchedule = selectedCategory === 'all' 
      ? schedule 
      : categories?.[selectedCategory] || [];
  
    return (
      <div>
        {/* Category filter */}
        {categories && Object.keys(categories).length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-3 py-1 rounded-full text-sm ${
                selectedCategory === 'all'
                  ? 'bg-accent text-white'
                  : 'bg-surface-muted text-ink-soft hover:bg-surface-muted'
              }`}
            >
              All Services ({schedule?.length || 0})
            </button>
            {Object.entries(categories).map(([cat, items]) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1 rounded-full text-sm capitalize ${
                  selectedCategory === cat
                    ? 'bg-accent text-white'
                    : 'bg-surface-muted text-ink-soft hover:bg-surface-muted'
                }`}
              >
                {cat} ({items.length})
              </button>
            ))}
          </div>
        )}
        
        {/* Schedule table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border-default">
            <thead className="bg-surface-page">
              <tr>
                <th className="px-2 sm:px-4 py-3 text-left text-small font-medium text-ink-muted uppercase">
                  Service
                </th>
                <th className="hidden sm:table-cell px-4 py-3 text-left text-small font-medium text-ink-muted uppercase">
                  Due At
                </th>
                <th className="px-2 sm:px-4 py-3 text-left text-small font-medium text-ink-muted uppercase">
                  Miles Until
                </th>
                <th className="hidden md:table-cell px-4 py-3 text-left text-small font-medium text-ink-muted uppercase">
                  Est. Cost
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-left text-small font-medium text-ink-muted uppercase">
                  Time
                </th>
                <th className="hidden md:table-cell px-4 py-3 text-left text-small font-medium text-ink-muted uppercase">
                  DIY?
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default">
              {filteredSchedule?.map((item, idx) => {
                const milesUntil = item.due_at_miles - currentMileage;
                const isOverdue = milesUntil < 0;
                const isDue = milesUntil <= 500 && milesUntil >= 0;
                
                return (
                  <tr key={idx} className={`hover:bg-surface-page ${
                    isOverdue ? 'bg-danger-tint' : isDue ? 'bg-warning-tint' : ''
                  }`}>
                    <td className="px-2 sm:px-4 py-3 text-sm text-ink-soft">
                      <div>
                        <div className="font-medium">{item.service}</div>
                        <div className="sm:hidden text-small text-ink-muted mt-1">
                          Due at {item.due_at_miles?.toLocaleString()} mi
                          <span className="ml-2">
                            {formatCostRange(item.estimated_cost)}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-sm text-ink-muted">
                      {item.due_at_miles?.toLocaleString()} mi
                    </td>
                    <td className={`px-2 sm:px-4 py-3 text-sm ${
                      isOverdue ? 'text-danger font-medium' : 
                      isDue ? 'text-warning font-medium' : 'text-ink-muted'
                    }`}>
                      {isOverdue ? 'Overdue by ' : ''}
                      {Math.abs(milesUntil).toLocaleString()} mi
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-sm text-ink-soft">
                      {formatCostRange(item.estimated_cost)}
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3 text-sm text-ink-muted">
                      {item.time_estimate} hrs
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-sm">
                      {item.can_diy ? (
                        <span className="text-success">Yes</span>
                      ) : (
                        <span className="text-ink-muted">Pro</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function CostAnalysis({ analysis, nextMajor }) {
    const costData = [
      { period: 'Next Year', amount: analysis?.next_year_estimate },
      { period: '3 Years', amount: analysis?.three_year_total, avg: analysis?.three_year_average },
      { period: '5 Years', amount: analysis?.five_year_total, avg: analysis?.five_year_average }
    ];

    // Totals are built only from services that HAVE an estimate. On a first
    // load none do, and an unqualified "$0" five-year total reads as
    // authoritative rather than absent (#131).
    const estimated = analysis?.coverage?.estimated ?? 0;
    const totalServices = analysis?.coverage?.total ?? 0;
    const isPartial = totalServices > 0 && estimated < totalServices;

    if (totalServices > 0 && estimated === 0) {
      return (
        <div className="space-y-6">
          <div className="bg-surface-page rounded-lg p-6 text-center">
            <h3 className="text-lg font-semibold mb-2">Maintenance Cost Projections</h3>
            <p className="text-sm text-ink-muted">
              Cost estimates for this vehicle are still being prepared. Your
              service schedule is shown above; check back shortly for costs.
            </p>
          </div>
          {nextMajor && (
            <div className="bg-warning-tint rounded-lg p-4">
              <h4 className="font-medium text-warning mb-2">Next Major Service</h4>
              <p className="text-sm text-warning">
                {nextMajor.services?.join(', ')} at {nextMajor.mileage?.toLocaleString()} miles
              </p>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Cost projections */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Maintenance Cost Projections</h3>
          {isPartial && (
            <p className="text-sm text-ink-muted mb-3">
              Based on {estimated} of {totalServices} scheduled services —
              estimates for the rest are still being prepared.
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {costData.map((item, idx) => (
              <div key={idx} className="bg-surface-page rounded-lg p-4">
                <p className="text-sm text-ink-muted mb-1">{item.period}</p>
                <p className="text-xl sm:text-2xl font-bold text-ink-soft">
                  ${item.amount?.toLocaleString() || 0}
                </p>
                {item.avg && (
                  <p className="text-sm text-ink-muted mt-1">
                    ${item.avg.toLocaleString()}/year avg
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
  
        {/* Cost per mile */}
        {analysis?.cost_per_mile && (
          <div className="bg-info-tint rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-info font-medium">Average Cost Per Mile</p>
                <p className="text-2xl font-bold text-info">
                  ${analysis.cost_per_mile.toFixed(3)}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-accent" />
            </div>
          </div>
        )}
  
        {/* Next major service */}
        {nextMajor && (
          <div className="bg-warning-tint rounded-lg p-4">
            <h4 className="font-medium text-warning mb-2">Next Major Service</h4>
            <p className="text-sm text-warning">
              {nextMajor.services?.join(', ')} at {nextMajor.mileage?.toLocaleString()} miles
            </p>
            <p className="text-sm text-warning mt-1">
              Estimated cost: {formatCostRange(nextMajor.estimated_cost)}
            </p>
          </div>
        )}
      </div>
    );
  }

  function ManufacturerNotes({ notes }) {
    return (
      <div className="bg-info-tint rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-accent mt-0.5" />
          <div>
            <h3 className="font-semibold text-info mb-2">Manufacturer Recommendations</h3>
            <ul className="space-y-1">
              {notes.map((note, idx) => (
                <li key={idx} className="text-sm text-info">• {note}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  function MaintenanceUpgradePrompt({ data }) {
    return (
      <div className="bg-surface-page rounded-lg p-8 text-center">
        <Wrench className="w-12 h-12 text-ink-muted mx-auto mb-4" />
        <h3 className="text-section-title font-semibold mb-2">Limited Maintenance Preview</h3>
        <p className="text-ink-muted mb-6">{data.message}</p>
        
        {/* Show limited preview if available */}
        {data.limited_data?.upcoming_services?.length > 0 && (
          <div className="mb-6 text-left max-w-md mx-auto">
            <p className="text-sm font-medium text-ink-soft mb-2">Next services due:</p>
            {data.limited_data.upcoming_services.slice(0, 3).map((service, idx) => (
              <div key={idx} className="text-sm text-ink-muted mb-1">
                • {service.service} in {service.miles_until_due?.toLocaleString()} miles
              </div>
            ))}
          </div>
        )}
        
        <button className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent-hover">
          Upgrade for Full Access
        </button>
      </div>
    );
  }

  function MaintenanceLoadingSkeleton() {
    return (
      <div className="space-y-4">
        <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-surface-muted rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-surface-muted rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-surface-muted rounded w-1/5"></div>
          </div>
        </div>
        <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
          <div className="animate-pulse">
            <div className="flex space-x-4 mb-4">
              <div className="h-10 bg-surface-muted rounded w-24"></div>
              <div className="h-10 bg-surface-muted rounded w-24"></div>
              <div className="h-10 bg-surface-muted rounded w-24"></div>
            </div>
            <div className="space-y-3">
              <div className="h-20 bg-surface-muted rounded"></div>
              <div className="h-20 bg-surface-muted rounded"></div>
              <div className="h-20 bg-surface-muted rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function MaintenanceError({ error, onRetry }) {
    return (
      <div className="bg-danger-tint rounded-lg p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-danger mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-danger mb-1">Error Loading Maintenance Data</h3>
        <p className="text-danger mb-4">{error}</p>
        <button
          onClick={onRetry}
          className="bg-danger text-white px-4 py-2 rounded hover:bg-danger/80 transition"
        >
          Try Again
        </button>
      </div>
    );
  }

// Mock data for demo
function getMockMaintenanceData() {
  return {
    vehicle_info: {
      vin: "1FADP5CU3FL118730",
      year: 2015,
      make: "Ford",
      model: "C MAX Hybrid",
      trim: "SE 4dr Hatchback CVT",
      current_mileage: 45000
    },
    data_source: "api_vin",
    upcoming_services: [
      {
        service: "Oil Change and Filter",
        due_at_miles: 50000,
        miles_until_due: 5000,
        estimated_cost: { low: 54, high: 79, average: 66 },
        severity: "high",
        can_diy: true,
        time_estimate: 0.5
      },
      {
        service: "Rotate Tires",
        due_at_miles: 50000,
        miles_until_due: 5000,
        estimated_cost: { low: 20, high: 40, average: 30 },
        severity: "medium",
        can_diy: true,
        time_estimate: 0.5
      }
    ],
    overdue_services: [
      {
        service: "Cabin Air Filter Replacement",
        due_at_miles: 40000,
        overdue_by_miles: 5000,
        estimated_cost: { low: 30, high: 60, average: 45 },
        risk_level: 2,
        severity: "medium",
        can_diy: true,
        time_estimate: 0.25
      }
    ],
    maintenance_schedule: [
      {
        service: "Oil Change and Filter",
        due_at_miles: 50000,
        estimated_cost: { low: 54, high: 79 },
        time_estimate: 0.5,
        can_diy: true,
        severity: "high"
      },
      {
        service: "Brake Inspection",
        due_at_miles: 60000,
        estimated_cost: { low: 50, high: 150 },
        time_estimate: 1.0,
        can_diy: false,
        severity: "critical"
      }
    ],
    service_categories: {
      engine: [
        {
          service: "Oil Change and Filter",
          due_at_miles: 50000,
          estimated_cost: { low: 54, high: 79 }
        }
      ],
      brakes: [
        {
          service: "Brake Inspection",
          due_at_miles: 60000,
          estimated_cost: { low: 50, high: 150 }
        }
      ]
    },
    maintenance_cost_analysis: {
      next_year_estimate: 500,
      three_year_total: 2400,
      three_year_average: 800,
      five_year_total: 4500,
      five_year_average: 900,
      cost_per_mile: 0.075
    },
    next_major_service: {
      services: ["Transmission Fluid Change"],
      mileage: 60000,
      estimated_cost: { low: 150, high: 300 },
      miles_away: 15000
    },
    completed_services: [
      {
        service_name: "Oil Change",
        completed_mileage: 40000,
        completed_at: "2024-01-15",
        actual_cost: 65,
        service_provider: "Quick Lube",
        notes: "Used synthetic oil"
      }
    ],
    manufacturer_recommendations: [
      "Consider severe driving schedule if you frequently drive in stop-and-go traffic",
      "Hybrid battery cooling system should be inspected annually"
    ]
  };
}